/**
 * Common constants shared across the application
 * Update these values for easy maintenance
 */

// Application Info
export const APP_INFO = {
  name: 'Candeza',
  fullName: 'Candeza ERP',
  tagline: 'Music School Management Platform',
  version: 'v1.0.0',
  website: 'www.candeza.com',
  email: 'info@candeza.com',
  phone: '+91 89516 38328',
  address: '3rd Floor, 215/10C, near Trinity Complex, Sarjapur, Sompura, Bengaluru, Karnataka 562125',
} as const;

// School / tenant identifiers (sourced from school config)
export const SCHOOLS = {
  rhythmAndBeats: {
    id: 'rhythm-and-beats',
    name: 'Rhythm and Beats Music and Dance School',
    shortName: 'Rhythm & Beats',
    slug: 'rhythm-and-beats',
    fullName: 'Rhythm and Beats Music and Dance School',
    tagline: 'Believe . Express . Achieve . Transform',
    email: 'rhythmnbeatsmusicschool@gmail.com',
    phone: '+91 89516 38328',
    whatsapp: 'https://wa.me/918951638328',
    address: '3rd Floor, 215/10C, near Trinity Complex, Sarjapur, Sompura, Bengaluru, Karnataka 562125',
    website: 'www.rhythmnbeats.com',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    invoicePrefix: 'RB',
    social: {
      instagram: 'https://www.instagram.com/RhythmBeatsBengaluru',
      facebook: 'https://www.facebook.com/profile.php?id=61579559114958',
      youtube: 'https://www.youtube.com/@RhythmBeatsBengaluru',
      googleReview: 'https://g.page/r/CX7jYfyAJHCcEBM/review',
    },
    hours: {
      work: 'Tue–Fri: 3 PM – 9 PM  |  Sat–Sun: 10 AM – 7 PM  |  Mon: Closed',
      enquiry: 'Mon–Sun: 9 AM – Midnight',
    },
  },
  humAndStrum: {
    id: 'hum-and-strum',
    name: 'Hum and Strum Music School \u2013 Hadosiddapura',
    shortName: 'Hum & Strum',
    slug: 'hum-and-strum',
    fullName: 'Hum and Strum Music School – Hadosiddapura',
    tagline: 'Learn . Play . Perform',
    email: 'humandstrum.hsp@gmail.com',
    phone: '+91 89516 91324',
    whatsapp: 'https://wa.me/918951691324',
    address: '3rd Floor, Mitta Plaza, near Sobha Royal Pavilion, Carmelaram, Hadosiddapura, Chikkakannalli, Bengaluru, Karnataka 560035',
    website: 'www.humandstrum.com',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    invoicePrefix: 'HS',
    social: {
      instagram: 'https://www.instagram.com/HumAndStrumBengaluru',
      facebook: '',
      youtube: '',
      googleReview: '',
    },
    hours: {
      work: 'Tue–Fri: 3 PM – 9 PM  |  Sat–Sun: 10 AM – 7 PM  |  Mon: Closed',
      enquiry: 'Mon–Sun: 9 AM – Midnight',
    },
  },
} as const;

// Common Button Labels
export const BUTTON_LABELS = {
  save: 'Save',
  saveChanges: 'Save Changes',
  cancel: 'Cancel',
  close: 'Close',
  delete: 'Delete',
  edit: 'Edit',
  add: 'Add',
  create: 'Create',
  update: 'Update',
  submit: 'Submit',
  confirm: 'Confirm',
  back: 'Back',
  next: 'Next',
  search: 'Search',
  filter: 'Filter',
  export: 'Export',
  import: 'Import',
  download: 'Download',
  upload: 'Upload',
  view: 'View',
  viewAll: 'View All',
  viewDetails: 'View Details',
  signIn: 'Sign In',
  signOut: 'Sign Out',
  signUp: 'Sign Up',
} as const;

// Common Status Labels
export const STATUS_LABELS = {
  active: 'Active',
  inactive: 'Inactive',
  pending: 'Pending',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  draft: 'Draft',
  paid: 'Paid',
  unpaid: 'Unpaid',
  overdue: 'Overdue',
  partiallyPaid: 'Partially Paid',
} as const;

// Common Filter Options
export const COMMON_FILTERS = {
  status: [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' },
  ],
  dateRange: [
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' },
  ],
} as const;

// Timezone Options
export const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
] as const;

// Validation Messages
export const VALIDATION_MESSAGES = {
  required: 'This field is required',
  invalidEmail: 'Please enter a valid email address',
  invalidPhone: 'Please enter a valid phone number',
  minLength: (min: number) => `Must be at least ${min} characters`,
  maxLength: (max: number) => `Must be no more than ${max} characters`,
  passwordMismatch: 'Passwords do not match',
} as const;

// Toast Messages
export const TOAST_MESSAGES = {
  saveSuccess: 'Changes saved successfully!',
  saveError: 'Failed to save changes. Please try again.',
  deleteSuccess: 'Deleted successfully!',
  deleteError: 'Failed to delete. Please try again.',
  createSuccess: 'Created successfully!',
  createError: 'Failed to create. Please try again.',
  updateSuccess: 'Updated successfully!',
  updateError: 'Failed to update. Please try again.',
  networkError: 'Network error. Please check your connection.',
} as const;

// Empty State Messages
export const EMPTY_STATES = {
  noData: 'No data available',
  noResults: 'No results found',
  noStudents: 'No students found',
  noLeads: 'No leads found',
  noStaff: 'No staff members found',
  noBookings: 'No bookings found',
  noInvoices: 'No invoices found',
  noSubjects: 'No subjects found',
  noPackages: 'No packages found',
} as const;

